import { FC } from 'react';
import {
  Box,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

export type DescriptiveDropdownOption = {
  value: string;
  label: string;
  desc: string;
};

type MenuOrigin = {
  vertical: 'top' | 'bottom' | 'center';
  horizontal: 'left' | 'right' | 'center';
};

interface DescriptiveDropdownProps {
  label?: string;
  value: string;
  options: DescriptiveDropdownOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  minWidth?: string;
  wrapperSx?: SxProps<Theme>;
  selectSx?: SxProps<Theme>;
  menuItemSx?: SxProps<Theme>;
  menuAnchorOrigin?: MenuOrigin;
  menuTransformOrigin?: MenuOrigin;
}

const DescriptiveDropdown: FC<DescriptiveDropdownProps> = ({
  label,
  value,
  options,
  onChange,
  disabled,
  minWidth = '200px',
  wrapperSx,
  selectSx,
  menuItemSx,
  menuAnchorOrigin = { vertical: 'bottom', horizontal: 'left' },
  menuTransformOrigin = { vertical: 'top', horizontal: 'left' },
}) => {
  const selectedOption = options.find((option) => option.value === value);
  const selectValue = selectedOption ? value : '';

  const handleChange = (event: SelectChangeEvent) => {
    onChange(event.target.value);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', ...wrapperSx }}>
      {label && (
        <Typography
          sx={{ fontSize: '12px', fontWeight: 500, color: '#6C6C6C' }}
          id={`${label}-label`}
        >
          {label}
        </Typography>
      )}
      <Select
        value={selectValue}
        onChange={handleChange}
        disabled={disabled}
        IconComponent={ArrowDropDownIcon}
        renderValue={() => selectedOption?.label ?? ''}
        MenuProps={{
          anchorOrigin: menuAnchorOrigin,
          transformOrigin: menuTransformOrigin,
          PaperProps: {
            sx: {
              marginTop: '4px',
              borderRadius: '12px',
              boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
              '& .MuiList-root': {
                padding: '0px',
              },
            },
          },
        }}
        sx={{
          boxShadow: 'none',
          '.MuiOutlinedInput-notchedOutline': { borderStyle: 'none' },
          '& .MuiSelect-select': {
            padding: 0,
          },
          '.MuiSvgIcon-root': {
            fill: '#2F736E !important',
          },
          borderRadius: '8px',
          border: '1px solid #E8EAEB',
          paddingY: '0.375rem',
          paddingLeft: '1rem',
          paddingRight: '0.75rem',
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          letterSpacing: '-0.35px',
          fontWeight: 500,
          color: '#162936',
          minWidth,
          ...selectSx,
        }}
      >
        {options?.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '2px',
              padding: '12px 16px',
              whiteSpace: 'normal',
              '&.Mui-selected': {
                backgroundColor: '#EEF2F5 !important',
              },
              ...menuItemSx,
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 700,
                lineHeight: '20px',
                color: '#162936',
              }}
            >
              {option.label}
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '16px',
                color: '#6C6C6C',
              }}
            >
              {option.desc}
            </Typography>
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

export default DescriptiveDropdown;
